import { createElement, FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, TableCellProps } from '@mui/material';

import {
  ImageWithSkeleton,
  SelectableProps,
  Table,
  TableColumn,
  TitleTypeChip,
} from '@components/base';
import { TagGrid } from '@components/content';
import HighlightedTypography from '@components/layout/DetailPanel/HighlightedTypography';
import { EpisodeInfo } from '@components/media';
import { SeriesCountDisplay } from '@components/media';
import { IndexName } from '@enums/IndexName';
import useGlobalViewMode from '@hooks/useGlobalViewMode';
import useMetadataContext from '@hooks/useMetadataContext';
import { useProviderNames } from '@hooks/useProviderNames';
import { SimpleTitle } from '@models/Title';
import { SearchCardActionProps } from '@modules/search/components/SearchResult/SearchGrid/SearchResultCard';
import { getImageWidthInList, getTitleImageUrlFromProvider } from '@utils/imageUtils';

export type TitleListBase = Pick<
  SimpleTitle,
  'masterId' | 'originalTitle' | 'providers' | 'series' | 'titleName' | 'type'
> &
  Partial<Pick<SimpleTitle, 'productionYear' | 'series'>>;

type TitleListProps<T extends TitleListBase> = SelectableProps & {
  actions?: FC<SearchCardActionProps>;
  customColumns?: TableColumn<T>[];
  disableProductionYearColumn?: boolean;
  disableProvidersColumn?: boolean;
  disableSeriesColumn?: boolean;
  emptyStateContext?: 'search' | false;
  idKey?: string & keyof T;
  indexName?: IndexName;
  isLoading?: boolean;
  onSelect?: (title: T) => void;
  titles: T[];
};

export function TitleList<T extends TitleListBase>({
  actions,
  customColumns = [],
  disableProductionYearColumn,
  disableProvidersColumn,
  disableSeriesColumn,
  emptyStateContext = 'search',
  idKey = 'masterId',
  indexName,
  isLoading,
  onSelect,
  selectable,
  titles,
}: TitleListProps<T>) {
  const { t } = useTranslation();
  const { imageType } = useGlobalViewMode();
  const { selectTitle } = useMetadataContext();
  const { getProviderList } = useProviderNames();

  const imageWidth = getImageWidthInList(imageType);

  const onClick = (title: T) =>
    onSelect
      ? onSelect(title)
      : selectTitle({
          masterId: title.masterId,
          providerIdentifier:
            indexName === IndexName.PlaybackOptions ? title.providers[0] : undefined,
        });

  const getColumns = () => {
    const columns: TableColumn<T>[] = [
      {
        disableSortable: true,
        key: 'image',
        label: '',
        TableCellProps: { minimalWidth: true, padding: 'checkbox' },
        value: title => (
          <Box width={imageWidth}>
            <ImageWithSkeleton
              alt={title.titleName}
              key={imageType}
              ratio={imageType}
              src={getTitleImageUrlFromProvider(
                title,
                imageType,
                indexName === IndexName.PlaybackOptions && title.providers.length > 0
                  ? title.providers[0]
                  : undefined,
                { width: imageWidth }
              )}
              style={{ display: 'flex', alignSelf: 'flex-start' }}
            />
          </Box>
        ),
      },
      {
        disableSortable: true,
        key: 'titleName',
        label: 'Title',
        value: title => (
          <Fragment>
            <HighlightedTypography
              asset={title}
              field='titleName'
              lineHeight='normal'
              noWrap
              title={t('titleName')}
              variant='body2'
            />

            <HighlightedTypography
              asset={title}
              color='textSecondary'
              display='block'
              field='originalTitle'
              noWrap
              title={t('originalTitle')}
              variant='caption'
            />
          </Fragment>
        ),
      },
    ];

    if (customColumns) columns.push(...customColumns);

    if (!disableSeriesColumn)
      columns.push({
        disableSortable: true,
        key: 'seriesInformation',
        label: 'Series information',
        value: title =>
          title.type === 'series' ? (
            <SeriesCountDisplay series={title.series} />
          ) : title.type === 'episode' ? (
            <EpisodeInfo title={title} />
          ) : null,
      });

    if (!disableProductionYearColumn)
      columns.push({
        disableSortable: true,
        key: 'productionYear',
        label: t('productionYear'),
        numeric: true,
        value: 'productionYear',
      });

    columns.push({
      disableSortable: true,
      key: 'type',
      label: t('type'),
      value: ({ type = undefined }) => (type ? <TitleTypeChip titleType={type} /> : null),
    });

    if (!disableProvidersColumn)
      columns.push({
        disableSortable: true,
        key: 'providers',
        label: t('providers'),
        numeric: true,
        value: ({ providers }) => (
          <TagGrid maxTags={6} softTags tags={getProviderList(providers)} />
        ),
      });

    if (actions)
      columns.push({
        TableCellProps: {
          minimalWidth: true,
          align: 'right' as TableCellProps['align'],
        },
        key: 'actions',
        label: '',
        value: (title: T) =>
          createElement(actions, { simpleTitle: title as unknown as SimpleTitle }, []),
      });

    return columns;
  };

  return (
    <Table<T>
      absolutePositioned={false}
      columns={getColumns()}
      disablePagination
      emptyStateContext={emptyStateContext}
      getTableRowProps={title => ({ onClick: () => onClick(title) })}
      idKey={idKey}
      items={titles}
      loading={isLoading}
      selectable={selectable}
      skeletonCount={titles.length || 12}
      tableSearch={false}
    />
  );
}
